// Status Request → Reply — /admin/status-reply
// Paste a PM's "give me an update on these WOs" email; each line is matched to a
// Field Radar job (never auto-created) and a copy-ready reply is drafted.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { EnvelopeSimpleOpen, MagicWand, Copy, CheckCircle, Warning, LinkSimple, ArrowSquareOut, Bell, Check, FloppyDisk, PencilSimple, Camera, Eye } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";

const STATUS_CLS = {
  completed: "bg-emerald-600 text-white", in_progress: "bg-cyan-600 text-white", assigned: "bg-sky-600 text-white",
  pool: "bg-amber-500 text-white", follow_up: "bg-yellow-400 text-slate-900", on_hold: "bg-slate-500 text-white",
};

const PLACEHOLDER = "Checking on this one — will confirm shortly";
const lineText = (it) => {
  if (!it.note) return it.reply_line;
  if (!it.matched && it.reply_line.endsWith(PLACEHOLDER)) return it.reply_line.slice(0, -PLACEHOLDER.length) + it.note;
  return `${it.reply_line} — ${it.note}`;
};

function ItemRow({ it, idx, onLink, quick, onNote, onSaveNote }) {
  const j = it.job;
  return (
    <div className={`p-3 border-2 ${it.matched ? "border-slate-300 bg-white" : "border-rose-300 bg-rose-50"}`} data-testid={`sr-item-${idx}`}>
      <div className="flex items-start gap-2 flex-wrap">
        {it.matched ? <CheckCircle weight="fill" size={18} className="text-emerald-600 mt-0.5"/> : <Warning weight="fill" size={18} className="text-rose-600 mt-0.5"/>}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-black uppercase tracking-widest text-slate-500">
            {it.unit && <span>Apt {it.unit} · </span>}{it.external_wo && <span className="font-mono">WO {it.external_wo}</span>}
          </div>
          <div className="text-sm font-bold text-slate-900">{it.issue}</div>
          {j ? (
            <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px]">
              <span className={`px-1.5 py-0.5 font-black uppercase tracking-widest text-[9px] ${STATUS_CLS[j.status] || "bg-slate-200 text-slate-700"}`}>{j.status.replace("_", " ")}</span>
              <Link to={`/admin/jobs/${j.id}`} className="font-bold underline inline-flex items-center gap-1" data-testid={`sr-open-job-${idx}`}>
                {j.work_order_number ? `WO#${j.work_order_number}` : j.title} <ArrowSquareOut size={11}/>
              </Link>
              {j.assigned_to_name && <span className="text-slate-600">· {j.assigned_to_name}</span>}
              {j.photos > 0 && <span className="text-slate-600">· {j.photos} photos</span>}
              {it.proof_url && <a href={it.proof_url} target="_blank" rel="noreferrer" className="font-bold text-[#00C4CC] underline inline-flex items-center gap-1" data-testid={`sr-proof-${idx}`}><Camera weight="fill" size={11}/> Photo link</a>}
              {it.proof_url && (j.proof_views > 0
                ? <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1" data-testid={`sr-proof-seen-${idx}`}><Eye weight="fill" size={10}/> Seen {j.proof_views}× · {new Date(j.proof_last_viewed_at).toLocaleString([], { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                : <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-[9px] font-black uppercase tracking-widest" data-testid={`sr-proof-unseen-${idx}`}>Not opened yet</span>)}
            </div>
          ) : (
            <button onClick={() => onLink(it)} data-testid={`sr-link-${idx}`}
              className="mt-1 h-8 px-2 border-2 border-rose-400 text-rose-700 bg-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
              <LinkSimple weight="bold" size={12}/> Link to an existing WO
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 text-xs font-medium text-slate-700 border-l-4 border-slate-300 pl-2" data-testid={`sr-line-${idx}`}>{lineText(it)}</div>
      <div className="mt-2">
        <div className="flex gap-1 flex-wrap">
          {quick.map((q, qi) => (
            <button key={qi} onClick={() => onNote(idx, it.note ? `${it.note}; ${q}` : q)} data-testid={`sr-quick-${idx}-${qi}`}
              className="h-7 px-2 border border-slate-300 bg-slate-50 hover:bg-[#00C4CC]/10 text-[10px] font-bold text-slate-700">{q}</button>
          ))}
        </div>
        <div className="mt-1 flex gap-1">
          <input value={it.note || ""} onChange={e => onNote(idx, e.target.value)} data-testid={`sr-note-${idx}`}
            placeholder="Add your note for this line (e.g. replaced P-trap 9/8, tenant confirmed draining)"
            className="flex-1 h-9 px-2 border-2 border-slate-300 text-xs font-medium focus:border-[#00C4CC] outline-none"/>
          {j && (
            <button onClick={() => onSaveNote(idx)} disabled={!it.note || it.note_saved === it.note} data-testid={`sr-save-note-${idx}`}
              title="Save this note on the WO too"
              className="h-9 px-2 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-40 inline-flex items-center gap-1">
              <FloppyDisk weight="fill" size={12}/> {it.note_saved === it.note ? "Saved to WO" : "Save to WO"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const rebuildDraft = (prev, items, tpl) => {
  const lines = items.map((it, i) => `${i + 1}. ${lineText(it)}`).join("\n");
  const marker = `${tpl.intro}\n\n`;
  const start = prev.indexOf(marker);
  const end = prev.indexOf(`\n\n${tpl.outro}`);
  if (start < 0 || end < 0) {
    const greeting = (prev.split("\n")[0] || "Hi,").trim();
    const signer = (prev.trim().split("\n").pop() || "").trim();
    const sig = signer && !/,$/.test(signer) && signer !== tpl.signoff ? `\n${signer}` : "";
    return `${greeting}\n\n${tpl.intro}\n\n${lines}\n\n${tpl.outro}\n\n${tpl.signoff}${sig}`;
  }
  return prev.slice(0, start + marker.length) + lines + prev.slice(end);
};

const DEFAULT_TPL = { intro: "Here's where each one stands:", outro: "I'll send photos/close-out notes as each completes.", signoff: "Thanks," };

function CustomizeCard({ tpl, setTpl, quick, setQuick }) {
  const [open, setOpen] = useState(false);
  const [draftTpl, setDraftTpl] = useState(tpl);
  const [phrases, setPhrases] = useState(quick.join("\n"));
  useEffect(() => { setDraftTpl(tpl); }, [tpl]);
  useEffect(() => { setPhrases(quick.join("\n")); }, [quick]);
  const save = async () => {
    try {
      const [t, q] = await Promise.all([
        api.post("/status-request/template", draftTpl),
        api.post("/status-request/quick", { phrases: phrases.split("\n").map(x => x.trim()).filter(Boolean) }),
      ]);
      setTpl(t.data); setQuick(q.data.phrases || []);
      toast.success("Responses saved");
    } catch (e) { toast.error("Could not save"); }
  };
  return (
    <div className="mt-4 rugged-card bg-white" data-testid="sr-customize">
      <button onClick={() => setOpen(o => !o)} data-testid="sr-customize-toggle"
        className="w-full p-3 flex items-center gap-2 text-left">
        <PencilSimple weight="fill" size={14} className="text-[#00C4CC]"/>
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Customize responses</span>
        <span className="ml-auto text-[10px] font-bold text-slate-400">{open ? "Hide" : "Edit opening, closing & quick phrases"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <label className="block"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Opening line</span>
              <input value={draftTpl.intro} onChange={e => setDraftTpl({ ...draftTpl, intro: e.target.value })} data-testid="sr-tpl-intro"
                className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm font-medium"/></label>
            <label className="block"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Closing line</span>
              <textarea value={draftTpl.outro} onChange={e => setDraftTpl({ ...draftTpl, outro: e.target.value })} rows={2} data-testid="sr-tpl-outro"
                className="mt-1 w-full p-2 border-2 border-slate-300 text-sm font-medium"/></label>
            <label className="block"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Sign-off</span>
              <input value={draftTpl.signoff} onChange={e => setDraftTpl({ ...draftTpl, signoff: e.target.value })} data-testid="sr-tpl-signoff"
                className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm font-medium"/></label>
          </div>
          <label className="block"><span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Quick phrases (one per line)</span>
            <textarea value={phrases} onChange={e => setPhrases(e.target.value)} rows={7} data-testid="sr-quick-edit"
              className="mt-1 w-full p-2 border-2 border-slate-300 text-sm font-medium"/></label>
          <button onClick={save} data-testid="sr-customize-save"
            className="md:col-span-2 h-10 bg-[#0f172a] text-white font-black uppercase text-[11px] tracking-widest">Save responses</button>
        </div>
      )}
    </div>
  );
}

export default function AdminStatusReply() {
  const [params, setParams] = useSearchParams();
  const [raw, setRaw] = useState("");
  const [hint, setHint] = useState("5346 Chew Ave");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [draft, setDraft] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [activeDraft, setActiveDraft] = useState(null);
  const [trusted, setTrusted] = useState("");
  const [quick, setQuick] = useState([]);
  const [tpl, setTpl] = useState(DEFAULT_TPL);
  const [trustedSaved, setTrustedSaved] = useState("");

  const loadDrafts = () => api.get("/status-request/drafts").then(r => setDrafts(r.data || [])).catch(() => {});
  useEffect(() => {
    loadDrafts();
    api.get("/status-request/quick").then(r => setQuick(r.data.phrases || [])).catch(() => {});
    api.get("/status-request/template").then(r => setTpl(r.data)).catch(() => {});
    api.get("/status-request/trusted").then(r => { const v = (r.data.domains || []).join(", "); setTrusted(v); setTrustedSaved(v); }).catch(() => {});
  }, []);
  const setNote = (idx, text) => {
    setRes(prev => {
      const items = prev.items.map((it, i) => i === idx ? { ...it, note: text } : it);
      setDraft(d => rebuildDraft(d, items, tpl));
      return { ...prev, items };
    });
  };
  const saveNote = async (idx) => {
    const it = res.items[idx];
    try {
      await api.post("/status-request/note", { job_id: it.job.id, text: it.note, external_wo: it.external_wo || "" });
      setRes(prev => ({ ...prev, items: prev.items.map((x, i) => i === idx ? { ...x, note_saved: x.note } : x) }));
      toast.success("Note saved on the WO");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save note"); }
  };
  const saveTrusted = async () => {
    try {
      const r = await api.post("/status-request/trusted", { domains: trusted.split(/[,\s]+/).filter(Boolean) });
      const v = (r.data.domains || []).join(", "); setTrusted(v); setTrustedSaved(v);
      toast.success("Trusted senders saved");
    } catch (e) { toast.error("Could not save"); }
  };
  useEffect(() => {
    const id = params.get("draft");
    if (!id) return;
    api.get("/status-request/drafts?include_done=true").then(r => {
      const d = (r.data || []).find(x => x.id === id);
      if (d) openDraft(d);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const openDraft = (d) => {
    setActiveDraft(d); setRaw(d.raw || ""); setDraft(d.draft || "");
    setRes({ items: d.items || [], matched: d.matched, total: d.total });
  };
  const markDone = async () => {
    if (!activeDraft) return;
    await api.post(`/status-request/drafts/${activeDraft.id}/done`);
    toast.success("Marked handled");
    setActiveDraft(null); setParams({}); loadDrafts();
  };

  const parse = async () => {
    setBusy(true);
    try {
      const r = await api.post("/status-request/parse", { raw, property_hint: hint });
      setRes(r.data); setDraft(r.data.draft);
      toast.success(`${r.data.matched} of ${r.data.total} matched`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not parse"); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(draft);
    toast.success("Reply copied — paste into Gmail");
  };
  const simulate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/status-request/simulate-email", { body: raw, subject: "Work request update (test)", from_address: "Test PM <test@example.com>" });
      toast.success("Inbound email simulated — check the bell"); openDraft(r.data); loadDrafts();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not simulate"); }
    finally { setBusy(false); }
  };
  const link = async (it) => {
    const id = window.prompt(`Paste the Field Radar job link or ID to link WO ${it.external_wo}:`, "");
    if (!id) return;
    const jobId = id.trim().split("/").pop();
    try {
      await api.post("/status-request/link", { job_id: jobId, external_wo: it.external_wo });
      toast.success("Linked — re-parsing"); parse();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not link"); }
  };

  return (
    <AdminShell>
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight flex items-center gap-2">
            <EnvelopeSimpleOpen weight="fill" size={30} className="text-[#00C4CC]"/> Status Reply
          </h1>
          <p className="mt-1 text-sm text-slate-600 font-medium">Paste the PM's "update on the following work requests" email. Each WO is matched to your jobs — nothing is created — and a reply is drafted for you to copy. Emails like this arriving in your synced Gmail are pre-drafted below automatically (never sent).</p>

          {drafts.length > 0 && (
            <div className="mt-4 rugged-card p-3 bg-amber-50 border-amber-400" data-testid="sr-drafts">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 inline-flex items-center gap-1"><Bell weight="fill" size={12}/> Waiting for your reply ({drafts.length})</div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {drafts.map(d => (
                  <button key={d.id} onClick={() => openDraft(d)} data-testid={`sr-draft-${d.id}`}
                    className={`text-left p-2 border-2 bg-white hover:bg-amber-100 ${activeDraft?.id === d.id ? "border-[#0f172a]" : d.priority ? "border-red-500" : "border-amber-300"}`}>
                    <div className="text-xs font-black truncate">{d.priority && <span className="text-red-600 mr-1" data-testid={`sr-priority-${d.id}`}>⚡ PRIORITY</span>}{d.from_address || "Unknown sender"}</div>
                    <div className="text-[11px] text-slate-600 truncate">{d.subject}</div>
                    <div className="text-[10px] font-bold text-slate-500 mt-0.5">{d.matched}/{d.total} matched · {new Date(d.received_at).toLocaleString()}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {activeDraft && (
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="sr-active-draft">
              <span className="text-xs font-bold text-slate-700">Reviewing draft for <span className="font-black">{activeDraft.from_address}</span></span>
              <button onClick={markDone} data-testid="sr-mark-done"
                className="h-8 px-3 bg-emerald-600 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1"><Check weight="bold" size={12}/> Mark handled</button>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rugged-card p-4 bg-white">
              <textarea value={raw} onChange={e => setRaw(e.target.value)} rows={12} data-testid="sr-raw"
                placeholder={"Could you give me an update on the following work request?\n1. Apartment 3 A – Mice activity. (WO-129714)\n2. Apartment 3 B – Hot water is not working. (WO – 129897)"}
                className="w-full p-3 border-2 border-slate-300 text-sm font-medium focus:border-[#00C4CC] outline-none"/>
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <label className="flex-1 min-w-[180px]">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Building (for Apt matching)</span>
                  <input value={hint} onChange={e => setHint(e.target.value)} data-testid="sr-hint"
                    className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm font-bold"/>
                </label>
                <button onClick={parse} disabled={busy || raw.trim().length < 5} data-testid="sr-parse"
                  className="tactile-btn h-10 px-4 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50">
                  <MagicWand weight="fill" size={14}/> {busy ? "Matching…" : "Match & Draft"}
                </button>
                <button onClick={simulate} disabled={busy || raw.trim().length < 5} data-testid="sr-simulate" title="Runs the exact Gmail-sync path: creates a pending draft + bell ping, sends nothing"
                  className="h-10 px-3 border-2 border-amber-500 text-amber-800 bg-amber-50 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-50">
                  <Bell weight="fill" size={12}/> Test as inbound email
                </button>
              </div>
            </div>

            <div className="rugged-card p-4 bg-white">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Reply draft {res && <span data-testid="sr-matched">· {res.matched}/{res.total} matched</span>}</span>
                <button onClick={copy} disabled={!draft} data-testid="sr-copy"
                  className="h-9 px-3 bg-[#00C4CC] text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1 disabled:opacity-50">
                  <Copy weight="bold" size={14}/> Copy reply
                </button>
              </div>
              <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={14} data-testid="sr-draft"
                placeholder="Your reply will appear here…"
                className="mt-2 w-full p-3 border-2 border-slate-300 text-sm font-medium focus:border-[#00C4CC] outline-none"/>
            </div>
          </div>

          <div className="mt-4 rugged-card p-3 bg-white flex flex-wrap items-end gap-2" data-testid="sr-trusted">
            <label className="flex-1 min-w-[240px]">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Trusted PM senders (domains or emails)</span>
              <input value={trusted} onChange={e => setTrusted(e.target.value)} placeholder="lasalle.edu, mario@4sbay.com" data-testid="sr-trusted-input"
                className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm font-bold"/>
            </label>
            <button onClick={saveTrusted} disabled={trusted === trustedSaved} data-testid="sr-trusted-save"
              className="h-10 px-3 bg-[#0f172a] text-white font-black uppercase text-[11px] tracking-widest disabled:opacity-40">Save</button>
            <p className="w-full text-[11px] text-slate-500 font-medium">Any email from these senders that mentions a WO number is drafted — no "update" wording needed — and flagged <span className="text-red-600 font-black">⚡ PRIORITY</span> in the bell.</p>
          </div>

          <CustomizeCard tpl={tpl} setTpl={setTpl} quick={quick} setQuick={setQuick}/>

          {res && (
            <div className="mt-4 space-y-2" data-testid="sr-items">
              {res.items.map((it, i) => <ItemRow key={i} it={it} idx={i} onLink={link} quick={quick} onNote={setNote} onSaveNote={saveNote}/>)}
              {res.matched < res.total && (
                <p className="text-[11px] text-slate-500 font-medium">Unmatched lines mean the WO was never entered in Field Radar. Use <Link to="/admin/wo-paste" className="underline font-bold">Text → WOs</Link> to bring them in, or "Link" if it already exists under a different number.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
